// ABOUTME: Centered modal for user feedback submission with validation and email notifications
// ABOUTME: Integrates with feedbackService for PocketBase storage and VPS email delivery
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import feedbackService from '../../services/feedbackService';
import './FeedbackModal.css';

const FeedbackModal = ({ isOpen, onClose, currentPage }) => {
  const { user } = useAuth();
  const [feedbackType, setFeedbackType] = useState('suggestion');
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  const charCount = feedbackText.length;
  const minChars = 2; // TD3-105: Minimum 2 chars, only validated on submit (UX-friendly)
  const maxChars = 5000; // TD3-105: Maximum 5000 chars, only shown when exceeded
  const isValid = charCount >= minChars && charCount <= maxChars;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (charCount < minChars) {
      setError(`O feedback deve ter pelo menos ${minChars} caracteres`);
      return;
    }

    if (charCount > maxChars) {
      setError(`O feedback não pode exceder ${maxChars} caracteres`);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // TD3-206: Pass nickname for email greeting (fallback to email if nickname missing)
      const result = await feedbackService.submitFeedback(
        user.id,
        user.email,
        feedbackText,
        feedbackType,
        currentPage,
        user.nickname || user.email
      );

      if (result.success) {
        setShowSuccess(true);
        // Auto-close after 2 seconds
        setTimeout(() => {
          setShowSuccess(false);
          setFeedbackText('');
          setFeedbackType('suggestion');
          onClose();
        }, 2000);
      } else {
        setError(result.error || 'Erro ao enviar feedback');
      }
    } catch (err) {
      setError('Erro ao enviar feedback. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFeedbackText('');
    setError('');
    setShowSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-modal-overlay" onClick={handleCancel}>
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        {showSuccess ? (
          <div className="feedback-success">
            <div className="success-icon">✓</div>
            <h3>Feedback Enviado!</h3>
            <p>Você receberá uma confirmação por e-mail.</p>
          </div>
        ) : (
          <>
            <div className="feedback-modal-header">
              <h2>Enviar Feedback</h2>
              <button
                className="feedback-modal-close"
                onClick={handleCancel}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="feedback-form">
              <div className="form-group">
                <label htmlFor="feedbackType">Tipo de Feedback</label>
                <select
                  id="feedbackType"
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                  className="feedback-select"
                  disabled={isSubmitting}
                >
                  <option value="bug">Bug/Erro</option>
                  <option value="suggestion">Sugestão</option>
                  <option value="praise">Elogio</option>
                  <option value="other">Outro</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="feedbackText">Sua Mensagem</label>
                <textarea
                  id="feedbackText"
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Compartilhe sua experiência, sugestões ou reporte problemas..."
                  className={`feedback-textarea ${!isValid && charCount > 0 ? 'invalid' : ''}`}
                  rows="6"
                  disabled={isSubmitting}
                />
                <div className="char-counter">
                  <span className={charCount > maxChars ? 'invalid' : ''}>
                    {charCount} {charCount > maxChars && `/ ${maxChars}`} caracteres
                  </span>
                  {charCount > maxChars && (
                    <span className="hint">Limite excedido em {charCount - maxChars} caracteres</span>
                  )}
                </div>
              </div>

              {error && <div className="feedback-error">{error}</div>}

              <div className="feedback-modal-actions">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-cancel"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={!isValid || isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Feedback'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
